/**
 * AUTHORIZATIONS — the paper trail, and the form that adds to it.
 *
 * The list exists because "show me every yes this shop has on file" is a
 * question an inspector asks, and it used to mean opening jobs one at a
 * time. The form exists because until now this build could only read.
 *
 * The form deliberately does NOT have a signature pad. On the phone there
 * is nothing to sign, and pretending otherwise is exactly what had staff
 * asking one customer four times. A signature belongs on the customer's
 * own screen, where their finger is.
 */

import { shopWords } from "../ui/words.ts";
import { listAuthorizations, recordAuthorization, type AuthRow } from "../data/authorizations.ts";
import { OFFER, OFFER_FINE } from "../domain/partsreturn.ts";
import { billAsTitle, type BillAs } from "../domain/billas.ts";
import { TEARDOWN_TITLE } from "../domain/teardown.ts";
import { esc, money, ago, mount, since } from "../ui/dom.ts";

const how = (a: AuthRow) =>
  a.method === "phone" ? "over the phone"
  : a.method === "portal" ? "signed on their own screen"
  : a.method === "in_person" ? "in person" : "at the counter";

export async function authorizationsPage(): Promise<void> {
  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>Authorizations</h1>
        <p>Every yes on file. Signed or spoken — both count, and both are here.</p></header>
      <div class="card" id="auth-list"><div class="loading">Reading&hellip;</div></div>
    </div>`);

  const host = document.getElementById("auth-list");
  if (!host) return;
  try {
    const rows = await listAuthorizations();
    const signed = rows.filter((r) => !r.declined && (r.signature ?? "").trim()).length;
    const spoken = rows.filter((r) => !r.declined && !(r.signature ?? "").trim()).length;
    const refused = rows.filter((r) => r.declined).length;

    host.innerHTML =
      `<div class="card-head">${rows.length} on file &middot; ${signed} signed &middot; ${spoken} spoken &middot; ${refused} refused</div>` +
      (rows.length ? rows.map((r) => `
        <a class="row" href="#/job/${r.job_id}">
          <div class="row-main">
            <div class="row-title">
              <span class="num">${esc(r.authorized_by)}</span>
              <span class="pill ${r.declined ? "s-cancelled" : (r.signature ?? "").trim() ? "s-paid" : "s-scheduled"}">
                ${r.declined ? "refused" : (r.signature ?? "").trim() ? "signed" : "spoken"}</span>
              ${r.jobs ? `<span class="pill">${esc(r.jobs.job_number)}</span>` : ""}
            </div>
            <div class="row-sub">${esc(how(r))}${
              r.jobs?.customers ? `<span class="dot">&middot;</span>${esc(r.jobs.customers.full_name)}` : ""}${
              r.declined && r.decline_reason ? `<span class="dot">&middot;</span>${esc(r.decline_reason)}` : ""}</div>
          </div>
          <div class="row-side">
            <div class="amount">${r.declined ? "—" : money(r.amount)}</div>
            <div class="when">${esc(since(r.authorized_at))}</div>
          </div>
        </a>`).join("") : `<div class="empty">Nothing on file yet.</div>`);
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

/**
 * The form, rendered into a host element on the job card.
 * `onDone` re-reads the job so the track and What-is-next move with it.
 */
/**
 * `papers` is what this job can have agreed to, with the figure for each.
 * More than one appears as a chooser, because BAR wants towing agreed
 * separately from the repair and one signature covering both is the thing
 * that is not allowed. One paper draws no chooser at all, which is almost
 * every job. See 0119.
 */
/**
 * `visit` is a garage door shop's free estimate visit (src/domain/
 * visitquote.ts): the yes is to somebody coming out, at no figure, so the
 * form records $0 and says what was agreed rather than asking for a price
 * that does not exist. Only the job card can say a job is one.
 */
export function authForm(hostId: string, jobId: string, suggested: number,
                         customerName: string, onDone: () => void,
                         papers: Array<{ paper: BillAs; amount: number }> = [],
                         visit = false): void {
  const host = document.getElementById(hostId);
  if (!host) return;

  /**
   * WHAT THEY TYPED SURVIVES A REDRAW. Neelum (NYIA), 2026-10-14: typed
   * 1500 as the figure agreed, pressed Record it, and was told "Enter the
   * figure they agreed to." with the box back at 0. The click redrew the
   * form into its saving state FIRST and read the fields afterwards, from
   * the fresh copy holding the default; the refusal then redrew it again.
   * Everything is read before any redraw now, and every redraw puts back
   * what was there.
   */
  let kept: Record<string, string> = {};
  let keptParts = false;
  const read = (): void => {
    const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value;
    for (const id of ["af-name", "af-method", "af-paper", "af-amount", "af-phone", "af-reason"]) {
      const x = val(id);
      if (x !== undefined) kept[id] = x;
    }
    const box = document.getElementById("af-parts") as HTMLInputElement | null;
    if (box) keptParts = box.checked;
  };
  const restore = (): void => {
    for (const [id, x] of Object.entries(kept)) {
      const el = document.getElementById(id) as HTMLInputElement | null;
      if (el) el.value = x;
    }
    const box = document.getElementById("af-parts") as HTMLInputElement | null;
    if (box) box.checked = keptParts;
  };

  const draw = (msg = "", busy = false, declining = false): void => {
    host.innerHTML = `
      <div class="authform">
        <div class="next-h">${visit ? "Record their yes to the free estimate visit" : "Record the customer's yes"}</div>
        <div class="fields">
          <label class="field"><span>Who agreed</span>
            <input id="af-name" value="${esc(customerName)}"></label>
          <label class="field"><span>How</span>
            <select id="af-method">
              <option value="phone">Over the phone</option>
              <option value="in_person">In person</option>
              <option value="counter">At the counter</option>
            </select></label>
          ${declining ? `
            <label class="field wide"><span>Why not</span>
              <input id="af-reason"></label>`
          : `
            ${papers.length > 1 ? `
              <label class="field"><span>Agreed to what</span>
                <select id="af-paper">${papers.map((p, i) =>
                  `<option value="${p.paper}" data-amount="${p.amount.toFixed(2)}" ${
                    i === 0 ? "selected" : ""}>${esc(billAsTitle(p.paper))}</option>`).join("")}
                </select></label>` : ""}
            ${visit ? `<p class="fine wide">No figure: they are saying yes to us coming out
              to look. The price is written at the door, and that gets its own yes.</p>`
            : `<label class="field"><span>Figure agreed</span>
              <input id="af-amount" type="number" step="0.01" value="${suggested.toFixed(2)}"></label>`}
            <label class="field"><span>Number called</span>
              <input id="af-phone"></label>
            ${/* The old-parts offer is a repair shop's duty. A trade whose
                  lines are expenses, not parts (investigations), has
                  nothing to hand back: Neelum read "I want my old parts
                  back" on a case, 2026-10-14. */ shopWords().partLabel !== "part" ? "" : `
            <label class="field wide af-parts">
              <input type="checkbox" id="af-parts">
              <span>${esc(OFFER)}</span></label>
            <p class="fine wide">${esc(OFFER_FINE)} Ask this now. The offer
              belongs at the authorization, not on the invoice.</p>`}`}
        </div>
        <div class="af-row">
          <button class="btn primary" id="af-save" ${busy ? "disabled" : ""}>
            ${busy ? "Saving…" : declining ? "Record the refusal" : "Record it"}</button>
          <button class="btn ghost sm" id="af-toggle">${declining ? "They said yes" : "They said no"}</button>
        </div>
        <div class="fine">No signature here on purpose — on a phone call there is nothing to sign, and
          who agreed and when is the record the law asks for. A signature belongs on their own screen.</div>
        ${msg ? `<p class="msg bad">${esc(msg)}</p>` : ""}
      </div>`;

    restore();
    const v = (id: string) => kept[id] ?? "";
    /* Picking what is being agreed to changes the figure with it, because
       the two are one fact and a tow agreed at the repair total is not a
       record of anything. */
    document.getElementById("af-paper")?.addEventListener("change", (ev) => {
      const sel = ev.target as HTMLSelectElement;
      const amt = sel.selectedOptions[0]?.dataset["amount"];
      const box = document.getElementById("af-amount") as HTMLInputElement | null;
      if (box && amt) box.value = amt;
    });
    document.getElementById("af-toggle")?.addEventListener("click", () => { read(); draw("", false, !declining); });
    document.getElementById("af-save")?.addEventListener("click", async () => {
      read();
      const paper = kept["af-paper"] as BillAs | undefined;
      const parts = keptParts;
      draw("", true, declining);
      try {
        await recordAuthorization({
          jobId,
          amount: visit ? 0 : Number(v("af-amount") || 0),
          freeVisit: visit,
          authorizedBy: v("af-name"),
          method: (v("af-method") || "phone") as "phone",
          phoneUsed: v("af-phone"),
          declined: declining,
          declineReason: v("af-reason"),
          /* Only meaningful on a yes. A refused job has no work and so no
             replaced parts to offer back. Undefined leaves it null, which
             reads as "nobody was asked" rather than as a no. */
          authorizes: paper || "repair",
          partsReturnWanted: declining ? undefined : parts,
        });
        onDone();
      } catch (e) {
        draw(e instanceof Error ? e.message : String(e), false, declining);
      }
    });
  };
  draw();
}

/**
 * THE TEARDOWN FORM — its own form, not an option on the other one.
 *
 * A teardown is agreed to BEFORE there are lines to agree to. The
 * ordinary form's figure comes from the job total and its paper chooser
 * comes from the bill_as values already on the lines, and at teardown
 * time there is no total and there are no lines: the whole point of
 * taking it apart is that nobody knows the price yet. So it gets its own
 * entry point rather than a fourth option in a chooser that is driven by
 * a quote that does not exist.
 *
 * WHAT MAKES THIS DIFFERENT FROM EVERY OTHER NAG IN THE COMPLIANCE WORK:
 * this one refuses. src/domain/teardown.ts has the argument in full. The
 * short version is that the disclosure is not a record of something that
 * happened elsewhere, it IS the thing, and half of it is worth nothing
 * rather than less.
 */
export function teardownForm(hostId: string, jobId: string,
                             customerName: string, onDone: () => void): void {
  const host = document.getElementById(hostId);
  if (!host) return;

  const draw = (msg = "", busy = false, declining = false): void => {
    host.innerHTML = `
      <div class="authform">
        <div class="next-h">${esc(TEARDOWN_TITLE)}</div>
        <p class="fine">Tell them all five of these before they agree, and record
          what you told them. A teardown is agreed to on its own terms,
          separately from the repair, because at this point nobody knows what
          the repair costs.</p>
        <div class="fields">
          <label class="field"><span>Who agreed</span>
            <input id="td-name" value="${esc(customerName)}"></label>
          <label class="field"><span>How</span>
            <select id="td-method">
              <option value="phone">Over the phone</option>
              <option value="in_person">In person</option>
              <option value="counter">At the counter</option>
            </select></label>
          ${declining ? `
            <label class="field wide"><span>Why not</span>
              <input id="td-reason"></label>`
          : `
            <label class="field"><span>Taking it apart costs</span>
              <input id="td-amount" type="number" step="0.01" min="0"></label>
            <label class="field"><span>Putting it back costs</span>
              <input id="td-reassembly" type="number" step="0.01" min="0"></label>
            <label class="field"><span>Parts destroyed by the teardown</span>
              <input id="td-destroyed" type="number" step="0.01" min="0"
                placeholder="0 if nothing"></label>
            <label class="field"><span>Most days to put it back</span>
              <input id="td-days" type="number" step="1" min="1" max="365"></label>
            <label class="field wide"><span>Can it go back to how it arrived</span>
              <select id="td-restorable">
                <option value="">Say yes or no</option>
                <option value="true">Yes, it can</option>
                <option value="false">No, it cannot</option>
              </select></label>
            <label class="field wide" id="td-note-wrap" hidden>
              <span>What cannot go back, and why</span>
              <input id="td-note" placeholder="The intake gaskets tear on removal"></label>
            <p class="fine wide">Enter 0 for parts destroyed if nothing will be.
              Leaving it empty is not the same answer and will not save.</p>`}
        </div>
        <div class="af-row">
          <button class="btn primary" id="td-save" ${busy ? "disabled" : ""}>
            ${busy ? "Saving…" : declining ? "Record the refusal" : "Record what they agreed to"}</button>
          <button class="btn ghost sm" id="td-toggle">${declining ? "They said yes" : "They said no"}</button>
        </div>
        ${msg ? `<p class="msg bad">${esc(msg)}</p>` : ""}
      </div>`;

    const v = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? "";
    /* An empty box and a zero are different answers, everywhere in this
       form. Number("") is 0, which would silently turn "nobody said" into
       "nothing will be destroyed" -- the exact confusion 0127 and
       src/domain/teardown.ts exist to prevent. */
    const num = (id: string): number | null => {
      const s = v(id).trim();
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    };

    /* "What cannot go back" only matters when the answer is no, and an
       always-visible box invites somebody to fill it in on a yes. */
    const sel = document.getElementById("td-restorable") as HTMLSelectElement | null;
    const noteWrap = document.getElementById("td-note-wrap");
    const syncNote = () => { if (noteWrap) noteWrap.hidden = sel?.value !== "false"; };
    sel?.addEventListener("change", syncNote);
    syncNote();

    document.getElementById("td-toggle")?.addEventListener("click", () => draw("", false, !declining));
    document.getElementById("td-save")?.addEventListener("click", async () => {
      const keep = {
        amount: num("td-amount"), reassembly: num("td-reassembly"),
        destroyed: num("td-destroyed"), days: num("td-days"),
        restorable: sel?.value ?? "", note: v("td-note"),
        name: v("td-name"), method: v("td-method"),
      };
      draw("", true, declining);
      try {
        await recordAuthorization({
          jobId,
          amount: keep.amount ?? 0,
          authorizedBy: keep.name,
          method: (keep.method || "phone") as "phone",
          declined: declining,
          declineReason: v("td-reason"),
          authorizes: "teardown",
          teardown: {
            reassemblyCost: keep.reassembly,
            destroyedCost: keep.destroyed,
            restorable: keep.restorable === "true" ? true
              : keep.restorable === "false" ? false : null,
            restoreNote: keep.note,
            maxDays: keep.days,
          },
        });
        onDone();
      } catch (e) {
        /* Redraw with the message AND put back what they typed. A form
           that empties itself on a validation error makes the person
           type five numbers again to fix one, and the second attempt is
           where the made-up number gets entered. */
        draw(e instanceof Error ? e.message : String(e), false, declining);
        const put = (id: string, val: string) => {
          const el = document.getElementById(id) as HTMLInputElement | null;
          if (el) el.value = val;
        };
        if (!declining) {
          put("td-amount", keep.amount === null ? "" : String(keep.amount));
          put("td-reassembly", keep.reassembly === null ? "" : String(keep.reassembly));
          put("td-destroyed", keep.destroyed === null ? "" : String(keep.destroyed));
          put("td-days", keep.days === null ? "" : String(keep.days));
          put("td-note", keep.note);
          const s2 = document.getElementById("td-restorable") as HTMLSelectElement | null;
          if (s2) { s2.value = keep.restorable; s2.dispatchEvent(new Event("change")); }
        }
        put("td-name", keep.name);
      }
    });
  };
  draw();
}
